import React, { useState, useRef, useEffect } from "react";
import styles from "./weight.module.css";
import { TextField } from "@mui/material";
import ElementTitle from "ui/crfs/element_title";
import DividerLine from "ui/crfs/divider_line";
import Radio from "@mui/material/Radio";
import { cloneDeep } from "lodash";
import DataCompleteChangeModal from "ui/crfs/data_complete_change_modal";
import EntryRuleOverrideModal from "ui/crfs/entry_rule_override_modal";
import WeightUI from "./components/weight_ui";
import ElementMenu from "ui/crfs/element_menu";
import LoadingButton from "@mui/lab/LoadingButton";

let tempStorage = null;

export default function Weight(props) {
  const {
    key_id,
    field,
    title,
    options,
    sub_title,
    uval,
    onUpdate,
    crf_id,
    field_id,
    query,
    setQuery,
    subject_id,
    domain_id,
    calcUpdateToCrfStatus,
    enableQuery,
    show_checkmark,
    length,
    units,
    placeHolder,
    read_only,
    crfStatus,
    setParentFieldError,
    toggleHistory,
    selected_audit_icon,
    setQueryModalData,
  } = props;

  const [valueObj, setValueObj] = useState((field && field.value_obj) || {});
  const [dataChange, setDataChange] = useState(false);

  const [showingCompleteChangeModal, setShowingCompleteChangeModal] = useState(false);
  const [showingEntryOverrideModal, setShowingEntryOverrideModal] = useState(false);

  useEffect(() => {
    tempStorage = field.value_obj;
  }, [field.value_obj]);

  const commonUpdateAction = (_valueObj = null, reason = null) => {
    if (!_valueObj) {
      _valueObj = valueObj;
    }
    if (_valueObj.value === tempStorage.value && _valueObj.value2 === tempStorage.value2) {
      return;
    }

    let obj = {
      data_id: field.value_obj?.data_id || null,
      flags: field.value_obj?.flags || null,
      value: _valueObj.value,
      value2: _valueObj.value2,
      value3: _valueObj.value3,
      value_name: field.value_name,
      value2_name: field.value2_name,
      value3_name: field.value3_name,
      reason,
    };
    if (reason) {
      obj.reason = reason;
    }
    onUpdate(obj);
  };

  const acceptTempStorage = (reason) => {
    commonUpdateAction(null, reason);
    tempStorage = valueObj;
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setDataChange(false);
  };

  const closeModal = () => {
    setShowingCompleteChangeModal(false);
    setShowingEntryOverrideModal(false);
    setValueObj(tempStorage);
    setDataChange(false);
  };

  const currentValueDisplay =
    (valueObj &&
      valueObj.value &&
      valueObj.value2 &&
      valueObj.value3 &&
      `${valueObj.value} ${valueObj.value2} (${valueObj.value3})`) ||
    "---";

  const updateValue = (error_override = false) => {
    if (entry_rule_error() && !error_override) {
      if (field.entry_rule_override) {
        setShowingEntryOverrideModal(true);
      } else {
        setValueObj(tempStorage);
        setDataChange(false);
      }
      return;
    } else {
      commonUpdateAction();
      setDataChange(false);
    }
  };

  const onChange = (key, value, onupdate = false) => {
    if (!dataChange) {
      tempStorage = valueObj;
    }
    setDataChange(true);
    let _valueObj = { ...valueObj, [key]: value };
    setValueObj(_valueObj);
    if (onupdate || ["value2", "value3"].includes(key)) {
      commonUpdateAction(_valueObj);
    }
  };

  const entry_rule_error = (error_override = false) => {
    let error = false;
    if (valueObj.value && valueObj.value2 && field.entry_rules && dataChange) {
      const lb_to_kg = (lb) => 0.453592 * lb;
      const kg_to_lb = (kg) => 2.20462 * kg;
      const round = (num, precision) => {
        var base = 10 ** precision;
        return (Math.round(num * base) / base).toFixed(precision);
      };

      field.entry_rules.some((rule) => {
        let rule_value = rule.value;
        if (valueObj.value2.includes("lb") && rule.unit === "kg") {
          rule_value = round(kg_to_lb(rule_value), 1);
        } else if (valueObj.value2.includes("kg") && rule.unit === "lb") {
          rule_value = round(lb_to_kg(rule_value), 1);
        }
        if (rule.rule_type === "min") {
          if (parseInt(valueObj.value) < rule_value) {
            error = `Must be greater than or equal to ${rule_value} ${valueObj.value2}`;
            return error;
          }
        } else if (rule.rule_type === "max") {
          if (parseInt(valueObj.value) > rule_value) {
            error = `Must be less than or equal to ${rule_value} ${valueObj.value2}`;
            return error;
          }
        }
      });
    }

    return error;
  };

  return (
    <div className={styles.outer_wrapper}>
      <div className={styles.input_flex}>
        <div className={styles.left_split}>
          {enableQuery ? (
            <div>
              <ElementTitle required={field.required} title={title} />
            </div>
          ) : (
            <div>{title}</div>
          )}
        </div>

        <div className={styles.right_split}>
          {field.read_only || read_only ? (
            <div>
              <div>{currentValueDisplay}</div>
              {field.value_obj?.missing ? <div className={styles.missing}>missing</div> : null}
            </div>
          ) : (
            <div className={styles.flex_column}>
              <div className={styles.flex_row}>
                <div className={styles.weight_wrapper}>
                  <WeightUI
                    {...props}
                    value={valueObj.value || ""}
                    unit={valueObj.value2 || ""}
                    type={valueObj.value3 || ""}
                    onChange={onChange}
                    onUpdate={updateValue}
                  />
                </div>
                <div className={styles.audit_icon}>
                  <ElementMenu {...props} />
                </div>
              </div>
              <div className={styles.flex_row}>
                {entry_rule_error().constructor === String ? (
                  <div className={styles.input_flex}>
                    <div className={styles.error}>{entry_rule_error()}</div>
                    {field.entry_rule_override ? (
                      <LoadingButton
                        variant="outlined"
                        loading={false}
                        onClick={() => updateValue(true)}
                        sx={{
                          fontSize: 14,
                          padding: "2px",
                          paddingLeft: "10px",
                          paddingRight: "10px",
                          marginLeft: "10px",
                        }}
                      >
                        Save Anyway
                      </LoadingButton>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
      <DividerLine />

      <DataCompleteChangeModal isOpen={showingCompleteChangeModal} close={closeModal} onYes={acceptTempStorage} />
      <EntryRuleOverrideModal isOpen={showingEntryOverrideModal} close={closeModal} onYes={acceptTempStorage} />
    </div>
  );
}
